import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, Building2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SiteImageUpload } from './SiteImageUpload';
import { MultiImageRenderUpload } from './MultiImageRenderUpload';
import {
  getExteriorRenderUrl,
  setExteriorRenderUrl,
} from '@/lib/admin/site-images';

interface ExteriorRenderUploadProps {
  block: 'A' | 'B';
  onImageUpdated?: (url: string) => void;
}

export function ExteriorRenderUpload({ block, onImageUpdated }: ExteriorRenderUploadProps) {
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadCurrentRender();
  }, [block]);

  const loadCurrentRender = async () => {
    try {
      setLoading(true);
      const url = await getExteriorRenderUrl(block);
      setCurrentUrl(url);
    } catch (error) {
      console.error(`Error loading exterior render for block ${block}:`, error);
      toast({
        title: 'Error',
        description: `Failed to load exterior render for Block ${block}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploaded = async (url: string) => {
    try {
      setSaving(true);
      const success = await setExteriorRenderUrl(block, url);
      if (success) {
        setCurrentUrl(url);
        if (onImageUpdated) {
          onImageUpdated(url);
        }
        toast({
          title: 'Success',
          description: `Exterior render for Block ${block} updated successfully`,
        });
      } else {
        throw new Error('Failed to save exterior render URL');
      }
    } catch (error) {
      console.error('Error saving exterior render:', error);
      toast({
        title: 'Error',
        description: 'Failed to save exterior render',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Block B (Lagoon View): use multi-image C-shape upload
  if (block === 'B') {
    return <MultiImageRenderUpload />;
  }

  // Block A (Canal View): single image upload
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Block {block} Exterior Render
        </CardTitle>
        <CardDescription>
          Upload the front-facing architectural render for Block {block}. This image is used in the Front-Facing Tracer tool for mapping unit positions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Warning:</strong> Replacing this render may affect existing traced coordinates if the image dimensions or layout change. 
            Ensure the new render matches the previous one's scale and orientation, or re-trace units after updating.
          </AlertDescription>
        </Alert>

        <SiteImageUpload
          imageType="exterior_render"
          existingImageUrl={currentUrl || undefined}
          onImageUpdated={handleImageUploaded}
          label={`Block ${block} Render`}
          description="Upload a high-quality architectural render. The image will be optimized while preserving aspect ratio for accurate tracing."
          block={block}
          maxImages={1}
          showWarning={true}
          warningMessage="Replacing this render may require re-tracing unit coordinates."
        />

        {currentUrl && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Current Render</h4>
            <div className="border rounded-lg overflow-hidden">
              <img
                src={currentUrl}
                alt={`Block ${block} exterior render`}
                className="w-full h-auto max-h-96 object-contain bg-muted"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              This render is currently being used in the Front-Facing Tracer tool.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

